import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../lib/apiClient';
import {
  ArrowLeft, Edit, Trash2, Copy, ChevronLeft, ChevronRight,
  Package, Tag, Clock, Layers, TrendingUp, Star, AlertTriangle,
  Loader2, ZoomIn, X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { fetchProduct, deleteProduct, duplicateProduct } from '../../api/supplierApi';
import SecureImage from '../../components/supplier/SecureImage';

// ── Linked Package Panel (Modular Stand only) ─────────────────────────────
function LinkedPackagePanel({ productId, tr }) {
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await apiClient.get(`/ultra-admin/modular-packages/by-stand/${productId}`);
        if (!cancelled) setPkg(r.data?.package || null);
      } catch {
        if (!cancelled) setPkg(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [productId]);

  if (loading) return null;
  if (!pkg) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 text-xs text-amber-800 flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <span>
          {tr
            ? 'Bu standa henüz bir Modüler Paket bağlanmamış. "Modular Packages" sayfasından oluşturun ve bu standı seçin.'
            : 'No Modular Package bound to this stand yet. Create one from the "Modular Packages" page and select this stand.'}
        </span>
      </div>
    );
  }

  const name = (tr ? (pkg.name_tr || pkg.name) : (pkg.name_en || pkg.name)) || pkg.name;
  const desc = (tr ? (pkg.description_tr || pkg.description) : (pkg.description_en || pkg.description));
  const items = pkg.items || [];
  return (
    <div className="bg-white border border-indigo-200 rounded-2xl overflow-hidden shadow-sm" data-testid="linked-package-panel">
      <div className="bg-gradient-to-r from-indigo-500 to-blue-600 px-5 py-3 text-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4" />
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest opacity-90">
              {tr ? 'Bağlı Modüler Paket' : 'Linked Modular Package'}
            </p>
            <h4 className="font-bold text-sm leading-tight">{name}{pkg.area_m2 ? ` · ${pkg.area_m2} m²` : ''}</h4>
          </div>
        </div>
      </div>
      <div className="p-4 space-y-3">
        {desc && <p className="text-xs text-gray-600 leading-relaxed">{desc}</p>}
        <div>
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">
            {tr ? `Paket İçeriği (${items.length})` : `Package Items (${items.length})`}
          </p>
          {items.length === 0 ? (
            <p className="text-xs text-gray-400 italic">{tr ? 'Kalem eklenmedi.' : 'No items.'}</p>
          ) : (
            <ul className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
              {items.map((it, i) => {
                const itemName = (tr ? (it.product?.name_tr || it.product?.name) : (it.product?.name_en || it.product?.name)) || '—';
                return (
                  <li key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-2 py-1.5 text-xs">
                    {it.product?.thumb_url
                      ? <img src={it.product.thumb_url} alt={itemName} loading="lazy" className="h-8 w-8 rounded object-cover flex-shrink-0" />
                      : <div className="h-8 w-8 rounded bg-gray-200 flex-shrink-0" />}
                    <span className="flex-1 font-medium text-gray-700 truncate">{itemName}</span>
                    <span className="font-bold text-gray-900">×{it.qty}</span>
                    {it.extra_allowed && (
                      <span title={tr ? 'Ekstra eklenebilir' : 'Extras allowed'} className="text-green-600 font-bold">+{tr ? 'Ekstra' : 'Extra'}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Gallery ────────────────────────────────────────────────────────────────
function Gallery({ images = [] }) {
  const [active, setActive] = useState(0);
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => { setActive(0); }, [images]);

  if (!images.length) {
    return (
      <div className="h-80 bg-gray-50 rounded-2xl flex items-center justify-center">
        <Package className="h-20 w-20 text-gray-200" />
      </div>
    );
  }

  const prev = () => setActive(i => (i - 1 + images.length) % images.length);
  const next = () => setActive(i => (i + 1) % images.length);

  return (
    <div className="space-y-3">
      {/* Main image */}
      <div className="relative h-80 bg-gray-50 rounded-2xl overflow-hidden group">
        <SecureImage
          src={images[active]?.url}
          alt="Ürün görseli"
          className="w-full h-full object-contain transition-transform duration-300"
        />
        {images.length > 1 && (
          <>
            <button onClick={prev}
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white shadow-lg rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <ChevronLeft className="h-4 w-4 text-gray-700" />
            </button>
            <button onClick={next}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white shadow-lg rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <ChevronRight className="h-4 w-4 text-gray-700" />
            </button>
          </>
        )}
        <button onClick={() => setZoomed(true)}
          className="absolute bottom-3 right-3 bg-white/90 hover:bg-white shadow rounded-xl p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <ZoomIn className="h-3.5 w-3.5 text-gray-700" />
        </button>
        {/* Counter */}
        {images.length > 1 && (
          <span className="absolute bottom-3 left-3 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">
            {active + 1} / {images.length}
          </span>
        )}
        {images[active]?.is_cover && (
          <span className="absolute top-3 left-3 bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
            <Star className="h-3 w-3" /> Kapak
          </span>
        )}
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {images.map((img, i) => (
            <button key={img.id} onClick={() => setActive(i)}
              className={`shrink-0 h-16 w-16 rounded-xl overflow-hidden border-2 transition-all ${i === active ? 'border-amber-500 shadow-md' : 'border-gray-100 hover:border-gray-300'}`}>
              <SecureImage src={img.thumb_url || img.url} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {zoomed && (
        <div className="fixed inset-0 z-[999] bg-black/95 flex items-center justify-center"
          onClick={() => setZoomed(false)}>
          <button className="absolute top-5 right-5 text-white hover:text-gray-300">
            <X className="h-8 w-8" />
          </button>
          {images.length > 1 && (
            <>
              <button onClick={e => { e.stopPropagation(); prev(); }}
                className="absolute left-5 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 rounded-full p-3 text-white">
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button onClick={e => { e.stopPropagation(); next(); }}
                className="absolute right-5 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 rounded-full p-3 text-white">
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}
          <SecureImage
            src={images[active]?.url}
            alt=""
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-2xl"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

// ── Variant Table ──────────────────────────────────────────────────────────
function VariantTable({ variants = [], currency = 'USD', tr }) {
  if (!variants.length) return null;
  return (
    <div>
      <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">{tr ? 'Varyantlar' : 'Variants'}</h4>
      <div className="border border-gray-100 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs">
            <tr>
              <th className="px-3 py-2 text-left">İsim</th>
              <th className="px-3 py-2 text-right">Fiyat</th>
              <th className="px-3 py-2 text-left">SKU</th>
              <th className="px-3 py-2 text-right">Stok</th>
            </tr>
          </thead>
          <tbody>
            {variants.map((v, i) => (
              <tr key={v.id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                <td className="px-3 py-2 font-medium text-gray-900">{v.name}</td>
                <td className="px-3 py-2 text-right text-amber-600 font-semibold">
                  {v.price != null ? `${currency} ${parseFloat(v.price).toFixed(2)}` : '—'}
                </td>
                <td className="px-3 py-2 text-gray-500 font-mono text-xs">{v.sku || '—'}</td>
                <td className="px-3 py-2 text-right text-gray-700">{v.stock ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Spec Item ──────────────────────────────────────────────────────────────
function Spec({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-2 text-sm">
      <Icon className="h-4 w-4 text-amber-500 shrink-0" />
      <span className="text-gray-500">{label}:</span>
      <span className="font-medium text-gray-800">{value}</span>
    </div>
  );
}

// ── Confirm Delete Modal ───────────────────────────────────────────────────
function ConfirmDelete({ title, onConfirm, onCancel, tr }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
            <AlertTriangle className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-sm">{tr ? 'Ürünü Sil' : 'Delete Product'}</h3>
            <p className="text-xs text-gray-500 mt-0.5">"{title}" {tr ? 'çöp kutusuna taşınacak.' : 'will be moved to trash.'}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onCancel} className="flex-1 rounded-xl">{tr ? 'İptal' : 'Cancel'}</Button>
          <Button onClick={onConfirm} className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl">{tr ? 'Sil' : 'Delete'}</Button>
        </div>
      </div>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────
const CATEGORY_TR_MAP = {
  'Organization':           'Organizatör',
  'Stand Constructor':      'Stand Yapımcısı',
  'Design Agency':          'Tasarım Ajansı',
  'Handling & Equipment':   'Taşıma & Ekipman',
  'Travel & Accommodation': 'Seyahat & Konaklama',
  'Furniture':              'Mobilya',
  'Catering':               'Catering',
};
const translateCat = (cat, tr) => tr ? (CATEGORY_TR_MAP[cat] || cat) : cat;

export default function ProductDetailPage({ productId, tr, onBack, onEdit, onDelete, onDuplicate, showLinkedPackage = false }) {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [duplicating, setDuplicating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = await fetchProduct(productId);
      setProduct(p);
    } catch {
      toast.error(tr ? 'Ürün yüklenemedi.' : 'Failed to load product.');
      onBack();
    } finally {
      setLoading(false);
    }
  }, [productId, tr, onBack]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteProduct(product.id);
      toast.success(tr ? 'Ürün çöp kutusuna taşındı.' : 'Product moved to trash.');
      onDelete?.();
      onBack();
    } catch (e) {
      toast.error(e.response?.data?.detail || (tr ? 'Silinemedi.' : 'Delete failed.'));
    } finally {
      setDeleting(false); setConfirmDelete(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-10 w-10 animate-spin text-amber-500" />
      </div>
    );
  }

  if (!product) return null;

  const images = product.image_objects || [];
  const variants = product.variants || [];

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors group">
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
          {tr ? 'Ürünler' : 'Products'}
        </button>
        <span className="text-gray-300">/</span>
        <span className="text-sm font-semibold text-gray-800 truncate max-w-xs">
          {tr ? (product.title_tr || product.title_en || product.title) : (product.title_en || product.title_tr || product.title)}
        </span>
      </div>

      {/* Main grid */}
      <div className="grid lg:grid-cols-2 gap-8">

        {/* Left: Gallery */}
        <Gallery images={images} />

        {/* Right: Info */}
        <div className="space-y-6">
          {/* Title + badges */}
          <div>
            <div className="flex items-start gap-2 mb-2">
              <h1 className="text-2xl font-bold text-gray-900 leading-tight flex-1">
                {tr ? (product.title_tr || product.title_en || product.title) : (product.title_en || product.title_tr || product.title)}
              </h1>
              <span className={`shrink-0 text-xs px-2.5 py-1 rounded-full font-medium mt-1 ${product.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                {product.is_active ? (tr ? 'Aktif' : 'Active') : (tr ? 'Pasif' : 'Inactive')}
              </span>
            </div>
            {product.category && (
              <span className="inline-block text-xs bg-amber-50 text-amber-700 px-2.5 py-1 rounded-full font-medium">
                <Tag className="h-3 w-3 inline mr-1" />{translateCat(product.category, tr)}
              </span>
            )}
          </div>

          {/* Price */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-4">
            {(() => {
              const pricing = product.pricing || {};
              const pricingLabels = { daily: 'Günlük', weekly: 'Haftalık', monthly: 'Aylık', one_time: 'Tek Seferlik', event_duration: 'Etkinlik Süresi' };
              const entries = Object.entries(pricing).filter(([, v]) => v != null);
              let effectivePrice = null;
              for (const k of ['event_duration', 'one_time', 'daily', 'weekly', 'monthly']) {
                if (pricing[k] != null) { effectivePrice = pricing[k]; break; }
              }
              if (effectivePrice == null) effectivePrice = product.price || 0;
              return entries.length > 0 ? (
                <>
                  <p className="text-xs text-amber-600 font-medium mb-2">Fiyatlandırma</p>
                  <div className="space-y-1">
                    {entries.map(([k, v]) => (
                      <div key={k} className="flex justify-between items-center">
                        <span className="text-xs text-amber-500">{pricingLabels[k] || k}</span>
                        <span className="font-bold text-amber-700">{product.currency} {parseFloat(v).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <p className="text-xs text-amber-600 font-medium mb-1">{tr ? 'Birim Fiyat' : 'Unit Price'}</p>
                  <p className="text-3xl font-extrabold text-amber-700">
                    {product.currency} {parseFloat(effectivePrice).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    {product.unit && <span className="text-base font-normal text-amber-500 ml-1">/ {product.unit}</span>}
                  </p>
                </>
              );
            })()}
          </div>

          {/* Specs */}
          <div className="space-y-2.5">
            <Spec icon={Layers} label={tr ? 'Min. Sipariş' : 'Min. Order'} value={product.min_order_quantity ? `${product.min_order_quantity} ${product.unit || ''}` : null} />
            <Spec icon={Clock} label={tr ? 'Tedarik Süresi' : 'Lead Time'} value={product.lead_time_days ? `${product.lead_time_days} ${tr ? 'gün' : 'days'}` : null} />
            <Spec icon={TrendingUp} label={tr ? 'Para Birimi' : 'Currency'} value={product.currency} />
          </div>

          {/* Description — hem TR hem EN admin önizlemede göster */}
          {(product.description_tr || product.description_en || product.description) && (
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-gray-500 uppercase">
                {tr ? 'Açıklama' : 'Description'}
              </h4>
              {/* Türkçe Açıklama */}
              {(product.description_tr || (!product.description_en && product.description)) && (
                <div className="rounded-xl bg-gray-50 border border-gray-100 px-4 py-3">
                  <p className="text-[10px] font-bold text-gray-400 uppercase mb-1.5 flex items-center gap-1">
                    <span>🇹🇷</span> Türkçe
                  </p>
                  <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {product.description_tr || product.description}
                  </p>
                </div>
              )}
              {/* English Description */}
              {product.description_en && (
                <div className="rounded-xl bg-gray-50 border border-gray-100 px-4 py-3">
                  <p className="text-[10px] font-bold text-gray-400 uppercase mb-1.5 flex items-center gap-1">
                    <span>🇬🇧</span> English
                  </p>
                  <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {product.description_en}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Variants */}
          <VariantTable variants={variants} currency={product.currency} tr={tr} />

          {/* Linked Modular Package (Ultra Admin only, for Modular Stands) */}
          {showLinkedPackage && <LinkedPackagePanel productId={product.id} tr={tr} />}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              data-testid="product-detail-edit-btn"
              onClick={() => onEdit(product)}
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-xl gap-2">
              <Edit className="h-4 w-4" />
              {tr ? 'Düzenle' : 'Edit'}
            </Button>
            <Button
              variant="outline"
              data-testid="product-detail-duplicate-btn"
              disabled={duplicating}
              onClick={async () => {
                setDuplicating(true);
                try {
                  const copy = await duplicateProduct(product.id);
                  toast.success(tr ? 'Ürün kopyalandı.' : 'Product duplicated.');
                  onDuplicate?.(copy);
                } catch { toast.error(tr ? 'Kopyalanamadı.' : 'Duplicate failed.'); }
                finally { setDuplicating(false); }
              }}
              className="rounded-xl gap-2">
              {duplicating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
              {tr ? 'Kopyala' : 'Copy'}
            </Button>
            <Button
              variant="outline"
              data-testid="product-detail-delete-btn"
              onClick={() => setConfirmDelete(true)}
              className="rounded-xl text-red-500 hover:bg-red-50 border-red-200 gap-2">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {confirmDelete && (
        <ConfirmDelete
          title={product.title}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
          tr={tr}
        />
      )}
    </div>
  );
}
